import { landingInstance as api } from '../axios/landingInstance'

export interface RemitaConfig {
  key: string;
  rrr?: string;
  transactionId: string;
  amount?: number;
  email?: string;
  customerId?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
}

export interface RemitaResponse {
  status: string;
  message: string;
  transactionId: string;
  rrr?: string;
  paymentReference?: string;
  amount?: number;
}

declare global {
  interface Window {
    RmPaymentEngine: any;
    env?: {
      RemitaWidgetUrl?: string;
    };
  }
}

class RemitaPaymentService {
  private scriptLoaded = false;
  private scriptUrl = process.env.NODE_ENV === "development"?   `${process.env?.REACT_APP_REMITA_WIDGET_URL}` :  `${window.env?.RemitaWidgetUrl}`
 
  /**
   * Load Remita payment script dynamically
   * This is more secure than adding it in index.html
   */
  private loadScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.scriptLoaded || window.RmPaymentEngine) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = this.scriptUrl;
      script.type = 'text/javascript';
      script.async = true;
      
      script.onload = () => {
        this.scriptLoaded = true;
        resolve();
      };
      
      script.onerror = () => {
        reject(new Error('Failed to load Remita payment script'));
      };

      document.head.appendChild(script);
    });
  }

  /**
   * Generate RRR on your backend before initiating payment
   * NEVER expose your merchant keys on the frontend
   */
  async generateRRR(paymentData: {
    amount: number;
    orderId: string;
    description: string;
    customerEmail: string;
    customerName: string;
  }): Promise<{ rrr: string; transactionId: string }> {
    try {
      // Call your backend API to generate RRR
      const response = await api.post('/payments/generate-rrr', paymentData);
      return response.data.data;
    } catch (error) {
      throw new Error('Failed to generate payment reference');
    }
  }

  

  /**
   * Initialize payment widget
   */
  async initializePayment(
    config: RemitaConfig,
    callbacks: {
      onSuccess: (response: RemitaResponse) => void;
      onError: (response: RemitaResponse) => void;
      onClose: () => void;
    }
  ): Promise<void> {
    try {
      // Load script first
      await this.loadScript();

      if (!window.RmPaymentEngine) {
        throw new Error('Remita payment engine not available');
      }

      // SECURITY: Get public key from environment variable
      // const publicKey = process.env.REACT_APP_REMITA_PUBLIC_KEY;
      
      // if (!publicKey) {
      //   throw new Error('Remita public key not configured');
      // }

      // Initialize payment engine
      const paymentEngine = window.RmPaymentEngine.init({
        // key: publicKey,
        key: "QzAwMDAyNzEyNTl8MTEwNjE4NjF8OWZjOWYwNmMyZDk3MDRhYWM3YThiOThlNTNjZTE3ZjYxOTY5NDdmZWE1YzU3NDc0ZjE2ZDZjNTg1YWYxNWY3NWM4ZjMzNzZhNjNhZWZlOWQwNmJhNTFkMjIxYTRiMjYzZDkzNGQ3NTUxNDIxYWNlOGY4ZWEyODY3ZjlhNGUwYTY=",
        processRrr: true,
        transactionId: config.transactionId,
        extendedData: {
          customFields: config.rrr ? [
            {
              name: "rrr",
              value: config.rrr
            }
          ] : []
        },
        onSuccess: (response: any) => {
          console.log('Payment successful:', response);
          callbacks.onSuccess(response);
        },
        onError: (response: any) => {
         console.error('Payment error:', response);
          callbacks.onError(response);
        },
        onClose: () => {
          console.log('Payment widget closed');
          callbacks.onClose();
        }
      });

      // Show payment widget
      paymentEngine.showPaymentWidget();
      
    } catch (error) {
      console.error('Payment initialization error:', error);
      throw error;
    }
  }

  /**
   * Verify payment status on your backend
   * CRITICAL: Always verify payment server-side
   */
  async verifyPayment(transactionId: string): Promise<{
    status: 'success' | 'failed' | 'pending';
    amount: number;
    transactionId: string;
    message: string;
    data?: any;
  }> {
    try {
      // Call your backend to verify payment with Remita
      const response = await api.post('/payments/verify', { transactionId });
      return response.data.data;
    } catch (error) {
      throw new Error('Failed to verify payment');
    }
  }

  /**
   * Clean up - remove script if needed
   */
  cleanup(): void {
    const script = document.querySelector(`script[src="${this.scriptUrl}"]`);
    if (script) {
      script.remove();
      this.scriptLoaded = false;
    }
  }
}

export const remitaPaymentService = new RemitaPaymentService();